import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { devWarn } from "@/lib/logger";
import { HEYCATCH_EVENTS, planTier, trackOutcomeOnce } from "@/lib/heycatch";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    // A transient poll failure is normal while Stripe finishes the charge, so we
    // keep retrying - but we no longer swallow the error silently: it is counted,
    // surfaced in the UI once retries are exhausted, and logged in development
    // only (never the response body, session id or any card detail).
    let consecutiveErrors = 0;
    const poll = async (n) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        consecutiveErrors = 0;
        if (data.payment_status === "paid") {
          const member = await refresh();
          setState("paid");
          // HeyCatch: a real, confirmed subscription - not merely a view of this
          // page. Guarded by the Stripe checkout session id, so refreshing or
          // re-opening the success URL cannot report the same purchase twice.
          // Only the coarse billing tier is sent; no card or payment detail.
          trackOutcomeOnce(
            `subscription:${sessionId}`,
            HEYCATCH_EVENTS.SUBSCRIPTION_STARTED,
            { plan: planTier(member) }
          );
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setState("failed");
          return;
        }
      } catch (err) {
        consecutiveErrors += 1;
        devWarn("SubscriptionSuccess.poll", err);
        // A 404 means the checkout session was never recorded for this account -
        // retrying cannot fix that, so stop and show the actionable state.
        if (err?.response?.status === 404) {
          setState("error");
          return;
        }
        if (consecutiveErrors >= 4) {
          setState("pending");
          return;
        }
      }
      if (n >= 10) {
        setState("pending");
        return;
      }
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 py-20 text-center">
      <div className="sm-card p-8 bg-white">
        {state === "checking" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#1F6F78]" />
            <h1 className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">
              Confirming your membership
            </h1>
            <p className="mt-2 text-sm text-[#4B5A60]">
              This usually takes a few seconds. Please do not close this page.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1F7A4D]" />
            <h1 data-testid="subscription-success-title" className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">
              You now have unlimited messaging
            </h1>
            <p className="mt-2 text-sm text-[#4B5A60]">
              Thank you for supporting Sponsor Me. Your membership renews automatically until you
              cancel, and you can cancel any time from your dashboard.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Button
                onClick={() => navigate("/messages")}
                className="flex-1 bg-[#1F6F78] hover:bg-[#185B62] text-white"
              >
                Go to messages
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/dashboard")}
                className="flex-1 bg-white border-[#E3DED4]"
              >
                My dashboard
              </Button>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-[#B7791F]" />
            <h1 className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">
              Still processing
            </h1>
            <p className="mt-2 text-sm text-[#4B5A60]">
              Your payment is taking a little longer than usual. Your membership will activate
              automatically. Check your dashboard in a few minutes.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="mt-6 bg-[#1F6F78] hover:bg-[#185B62] text-white"
            >
              Go to dashboard
            </Button>
          </>
        )}
        {(state === "failed" || state === "error") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-[#B42318]" />
            <h1 className="mt-4 font-display text-2xl font-bold text-[#1F2A2E]">
              We could not confirm a payment
            </h1>
            <p className="mt-2 text-sm text-[#4B5A60]">
              No membership was started. You can try again, or use the free Recovery Access Pass if
              cost is the barrier.
            </p>
            <Button
              onClick={() => navigate("/pricing")}
              className="mt-6 bg-[#1F6F78] hover:bg-[#185B62] text-white"
            >
              Back to pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
